import i18next from 'i18next';

import MenuPathMap from '../../MenuPathMap';
import { NavigationItem } from '../../types';

export type ProductsCatalogMenuOptions = {
  /** Include Catalog (NEW) — CatalogUI permission + allowed env. */
  includeCatalog?: boolean;
  /** Include Packaging → Entitlements — FeaturesEntitlements. */
  includeEntitlements?: boolean;
  /** Include Catalog Rules and Offer Management links — ArbiterAuthor or ArbiterReviewer. */
  includeArbiterGraphLinks?: boolean;
  /** Include Packaging → Configurable Bundles — ConfigurableBundle. */
  includeConfigurableBundles?: boolean;
  /** Include Packaging → Fixed Bundles — Bundling. */
  includeFixedBundles?: boolean;
  /** Include Packaging → Features — FeaturesEntitlements. */
  includeFeatures?: boolean;
  /** Prefer API Product Catalog href when present. */
  productCatalogOriginalHref?: string;
};

const pinnableItem = (
  partial: Pick<NavigationItem, 'id' | 'label' | 'originalHref' | 'menuPath'> &
    Partial<Pick<NavigationItem, 'endChip' | 'endChipType' | 'fullUrlMatch'>>,
): NavigationItem => ({
  chip: '',
  pinnable: true,
  divider: false,
  expandable: false,
  ...partial,
});

/**
 * Builds the Products flyout: Product Catalog | Packaging | Offer Management.
 * Omits Propositions and Promotions columns.
 */
export const buildProductsPopover = (
  options: ProductsCatalogMenuOptions = {},
): NonNullable<NavigationItem['popover']> => {
  const {
    includeCatalog = false,
    includeEntitlements = false,
    includeArbiterGraphLinks = false,
    includeConfigurableBundles = false,
    includeFixedBundles = false,
    includeFeatures = false,
    productCatalogOriginalHref = '/apps/Product.do?menu=Z-Billing',
  } = options;

  const productCatalogColumnItems: NavigationItem[] = [
    pinnableItem({
      id: 'Zuora/Products/ProductCatalog',
      label: i18next.t('GlobalNav.Products.ProductCatalog'),
      originalHref: productCatalogOriginalHref,
      menuPath: [MenuPathMap['Zuora/Products/ProductCatalog']],
    }),
  ];

  if (includeCatalog) {
    productCatalogColumnItems.push(
      pinnableItem({
        id: 'Zuora/Products/CatalogDemo',
        label: i18next.t('GlobalNav.Insertion.Catalog'),
        originalHref: '/platform/apps/catalog',
        endChip: i18next.t('GlobalNav.Chips.New'),
        endChipType: 'success',
        menuPath: [],
        fullUrlMatch: true,
      }),
    );
  }

  if (includeArbiterGraphLinks) {
    productCatalogColumnItems.push(
      pinnableItem({
        id: 'Zuora/Products/CatalogRules',
        label: i18next.t('GlobalNav.Products.CatalogRules'),
        originalHref: '/platform/apps/catalog/rules',
        menuPath: ['/platform/apps/catalog/rules'],
        fullUrlMatch: true,
      }),
    );
  }

  const packagingItems: NavigationItem[] = [];

  if (includeConfigurableBundles) {
    packagingItems.push(
      pinnableItem({
        id: 'Zuora/Products/Packaging/ConfigurableBundles',
        label: i18next.t('GlobalNav.Products.ConfigurableBundles'),
        originalHref: '/platform/apps/catalog/configurable-bundle/new',
        menuPath: ['/platform/apps/catalog/configurable-bundle/new'],
        fullUrlMatch: true,
      }),
    );
  }

  if (includeFixedBundles) {
    packagingItems.push(
      pinnableItem({
        id: 'Zuora/Products/Packaging/FixedBundles',
        label: i18next.t('GlobalNav.Products.FixedBundles'),
        originalHref: '/platform/apps/catalog/bundle',
        menuPath: ['/platform/apps/catalog/bundle'],
        fullUrlMatch: true,
      }),
    );
  }

  if (includeFeatures) {
    packagingItems.push(
      pinnableItem({
        id: 'Zuora/Products/Packaging/Features',
        label: i18next.t('GlobalNav.Products.Features'),
        originalHref: '/platform/apps/catalog/features',
        menuPath: ['/platform/apps/catalog/features'],
        fullUrlMatch: true,
      }),
    );
  }

  if (includeEntitlements) {
    packagingItems.push(
      pinnableItem({
        id: 'Zuora/Products/Packaging/Entitlements',
        label: i18next.t('GlobalNav.Products.Entitlements'),
        originalHref: '/platform/apps/feature-entitlements-dashboard',
        menuPath: ['/platform/apps/feature-entitlements-dashboard'],
        fullUrlMatch: true,
      }),
    );
  }

  return {
    label: i18next.t('GlobalNav.Products.ProductCatalog'),
    id: 'Zuora/Products-popover',
    items: productCatalogColumnItems,
    additionalItems: [
      ...(packagingItems.length
        ? [
            {
              label: i18next.t('GlobalNav.Products.Packaging'),
              id: 'Zuora/Products/Packaging',
              items: packagingItems,
            },
          ]
        : []),
      ...(includeArbiterGraphLinks
        ? [
            {
              label: i18next.t('GlobalNav.Products.OfferManagement'),
              id: 'Zuora/Products/OfferManagement',
              items: [
                pinnableItem({
                  id: 'Zuora/Products/OfferManagement/ProductRecommendations',
                  label: i18next.t('GlobalNav.Products.ProductRecommendations'),
                  originalHref:
                    '/platform/apps/catalog/rules?group=recommendation',
                  menuPath: [
                    '/platform/apps/catalog/rules?group=recommendation',
                  ],
                  fullUrlMatch: true,
                }),
                pinnableItem({
                  id: 'Zuora/Products/OfferManagement/SubscriberLifecycle',
                  label: i18next.t('GlobalNav.Products.SubscriberLifecycle'),
                  originalHref:
                    '/platform/apps/catalog/rules?group=subscriber_lifecycle',
                  menuPath: [
                    '/platform/apps/catalog/rules?group=subscriber_lifecycle',
                  ],
                  fullUrlMatch: true,
                }),
              ],
            },
          ]
        : []),
    ],
  };
};
